import React, { useState } from 'react';
import { IoStar, IoStarOutline, IoClose } from 'react-icons/io5';
import manDates from '../data/dates.json';
import womanDates from '../data/woman_dates.json';
import './DatesPage.css';
import { usePov } from '../context/PovContext';

interface DateInfo {
  day: number;
  avatar: string;
  name: string;
  venue: string;
  address: string;
  rating: number;
  feedback: string;
}

const DatesPage: React.FC = () => {
  const { pov } = usePov();
  const datesData = pov === 'woman' ? womanDates : manDates;
  const [selectedDate, setSelectedDate] = useState<DateInfo | null>(null);
  const [userRatings, setUserRatings] = useState<{ [key: number]: number }>({});
  const [userFeedback, setUserFeedback] = useState<{ [key: number]: string }>({});
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  // Generate random days for dates (3 per month)
  const generateRandomDays = (maxDay: number): number[] => {
    const days: number[] = [];
    while (days.length < 3) {
      const randomDay = Math.floor(Math.random() * (maxDay - 1)) + 1; // 1 to maxDay-1
      if (!days.includes(randomDay)) {
        days.push(randomDay);
      }
    }
    return days.sort((a, b) => a - b);
  };

  // Get current month and next month
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = (month: number, year: number) => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const randomDays = generateRandomDays(daysInMonth);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const hasDate = randomDays.includes(day);
      const dateInfo = hasDate ? datesData[randomDays.indexOf(day)] : null;

      days.push(
        <div
          key={day}
          className={`calendar-day ${hasDate ? 'has-date' : ''}`}
          onClick={() => hasDate && dateInfo && setSelectedDate(dateInfo)}
          style={hasDate && dateInfo ? {
            backgroundImage: `url(${dateInfo.avatar})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}}
        >
          <span className="day-number">{day}</span>
        </div>
      );
    }

    return (
      <div className="calendar">
        <div className="calendar-header">
          <h3>{monthNames[month]} {year}</h3>
        </div>

        <div className="calendar-grid">
          {days}
        </div>
      </div>
    );
  };

  const handleRating = (rating: number) => {
    if (selectedDate) {
      setUserRatings(prev => ({ ...prev, [selectedDate.day]: rating }));
    }
  };

  const handleFeedbackChange = (feedback: string) => {
    if (selectedDate) {
      setUserFeedback(prev => ({ ...prev, [selectedDate.day]: feedback }));
    }
  };

  const renderStars = (currentRating: number) => {
    return (
      <div className="star-rating-large">
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= (hoveredRating || currentRating);
          const StarIcon = isFilled ? IoStar : IoStarOutline;
          
          return (
            <button
              key={star}
              className="star-button-large"
              onClick={() => handleRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
            >
              {React.createElement(StarIcon as React.ComponentType<any>)}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="dates-container">
        <div className="dates-header">
          <h1>My Dates</h1>
        </div>

        <div className="calendars-container">
          {renderCalendar(currentMonth, currentYear)}
          {renderCalendar(nextMonth, nextYear)}
        </div>

        {selectedDate && (
          <div className="date-modal-overlay" onClick={() => setSelectedDate(null)}>
            <div className="date-modal" onClick={(e) => e.stopPropagation()}>
              <button className="close-button" onClick={() => setSelectedDate(null)}>
                {React.createElement(IoClose as React.ComponentType<any>)}
              </button>
              
              <div className="date-details">
                <div className="date-profile">
                  <img src={selectedDate.avatar} alt={selectedDate.name} className="date-avatar-large" />
                  <h2>{selectedDate.name}</h2>
                </div>
                
                <div className="venue-info">
                  <h3>{selectedDate.venue}</h3>
                  <p>{selectedDate.address}</p>
                </div>

                <div className="rating-section">
                  <h4>Rate your date:</h4>
                  {renderStars(userRatings[selectedDate.day] || 0)}
                </div>

                <div className="feedback-section">
                  <h4>Leave feedback:</h4>
                  <textarea
                    className="feedback-textarea"
                    placeholder="How was your date? Share your thoughts..."
                    value={userFeedback[selectedDate.day] || ''}
                    onChange={(e) => handleFeedbackChange(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatesPage;
